import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

export interface CompanyDetail {
  id: string;
  name: string;
  slug: string;
  cnpj: string;
  description: string | null;
  logoUrl: string | null;
  websiteUrl: string;
  primaryColor: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export class CompanyDetailFetchError extends Error {
  readonly name = 'CompanyDetailFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchCompanyById(id: string, signal?: AbortSignal): Promise<CompanyDetail> {
  const res = await fetchClient<CompanyDetail>({
    method: 'GET',
    url: `/companies/${id}`,
    signal,
  });
  if (res.error) {
    throw new CompanyDetailFetchError(
      `Failed to load company (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/**
 * TanStack Query options for `GET /companies/:id`. The edit page seeds the
 * form from this query — `staleTime: 0` so a fresh fetch happens on every
 * mount (form data should reflect the latest server state, not a cached one).
 */
export function companyByIdQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['recruiter', 'companies', 'byId', id] as const,
    queryFn: ({ signal }) => fetchCompanyById(id, signal),
    staleTime: 0,
    retry: (failureCount, err) => {
      if (err instanceof CompanyDetailFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
